"use client";

import { StockStatus } from "@/services/product/product.config";
import useTranslate from "@app/_hooks/useTranslate";
import { Paragraph } from "@ui/paragraph";
import { ComponentProps } from "react";
import ProductCard from "../../../components/product-card/ProductCard";
import type { LightsourceRow } from "../utils/lightsourceRow";
import {
  setLightsourceParam,
  useLightsourceParam,
} from "../utils/lightsourceSelection";

export default function ProductLightsource({
  row,
  quantity,
  locale,
  lightsourceParam,
  recommendedLabel,
  detailsLabel,
}: {
  row: LightsourceRow;
  quantity: number;
  locale: string;
  lightsourceParam: string | null;
  recommendedLabel: string;
  detailsLabel: string;
}) {
  const param = useLightsourceParam(lightsourceParam);
  const checked =
    !!param &&
    param !== "no-lightsource" &&
    (row.variantKey === param || row.key === param);

  function handleChange() {
    if (checked) return;
    setLightsourceParam(row.variantKey);
  }

  return (
    <ProductCard>
      <label
        htmlFor={row.variantKey}
        className="@container/lightsource grid grid-cols-[96px_1fr] items-center"
      >
        {row.image ? (
          <ProductCard.ImageFlat
            src={row.image.src}
            alt={row.image.alt}
            width={96}
            height={96}
          />
        ) : (
          <div className="size-24 bg-gray-100" />
        )}
        <ProductCard.Content className="flex-row gap-4">
          <input
            type="checkbox"
            id={row.variantKey}
            value={row.variantKey}
            checked={checked}
            onChange={handleChange}
            className="accent-black"
          />
          <div className="flex w-full flex-col">
            <ProductCard.Link
              href={row.link}
              locale={locale}
              className="self-end underline"
              product={row.tracked}
            >
              {detailsLabel}
            </ProductCard.Link>
            <div className="grid grid-cols-[1fr_auto] gap-x-4">
              <ProductCard.Title className="w-full overflow-hidden text-ellipsis whitespace-nowrap">
                {row.title}
              </ProductCard.Title>
              <ProductCard.Price priceInfo={row.priceInfo}>
                <Paragraph className="all-small-caps">
                  {quantity}&nbsp;x&nbsp;
                </Paragraph>
              </ProductCard.Price>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-x-4">
              <LightsourceStock code={row.stockCode} />
              {row.recommended ? (
                <Paragraph className="col-start-2 justify-self-end border px-2">
                  {recommendedLabel}
                </Paragraph>
              ) : null}
            </div>
          </div>
        </ProductCard.Content>
      </label>
    </ProductCard>
  );
}

function LightsourceStock({
  code,
  ...props
}: { code: StockStatus } & ComponentProps<typeof Paragraph>) {
  const t = useTranslate();

  if (
    code === StockStatus.Incoming ||
    code === StockStatus.PendingSoldOut ||
    code === StockStatus.PendingOutOfStock ||
    code === StockStatus.StockItem
  ) {
    return (
      <Paragraph {...props}>
        {code === StockStatus.Incoming ? t("inStockSoon") : t("inStock")}
      </Paragraph>
    );
  }

  if (code === StockStatus.OutOfStock || code === StockStatus.SoldOut) {
    return (
      <Paragraph {...props}>
        {code === StockStatus.OutOfStock ? t("outOfStock") : t("soldOut")}
      </Paragraph>
    );
  }

  return <span />;
}
